import { createRef, useEffect, useMemo } from "react";
import { createRoot } from "react-dom/client";
import { createBrowserHistory } from "history";
import { ErrorBoundary } from "react-error-boundary";
import TagManager from "react-gtm-module";
import { Helmet } from "react-helmet";
import { Routes } from "react-router";
import { Outlet, unstable_HistoryRouter as HistoryRouter } from "react-router-dom";
import {
  AppConfig,
  AppConfigContext,
  AppVersionChecker,
  BreadcrumbsContextProvider,
  defaultAppConfig,
  GlobalErrorBanner,
  MimicContextProvider,
  PathPermissionsContextProvider,
  ReactModal,
  renderRoute,
  RoutesContext,
  useRoutesContext,
} from "@tecorien/mimic";
import { MimicContextProvider as Mimic2ContextProvider, setModalAppElement } from "@tecorien/mimic-v2";
import { ApiProvider } from "components/ApiProvider";
import { routes } from "pages/routes";
import { ClientContext, ClientContextProvider } from "./components/ClientContext";
import { ModalProvider } from "./components/modals";
import { RoutePermissionChecker } from "./components/RoutePermissionChecker";
import { config } from "./config";
import { links } from "./links";
import { RouteDescription } from "./types";

import "@tecorien/mimic/build/library.css";
import "@tecorien/mimic/build/phoneNumber.css";
import "./styles.scss";
import "@tecorien/mimic-v2/build/styles/defaultTheme.scss";
import "@tecorien/mimic-v2/build/index.css";

ReactModal.setAppElement("#root");
setModalAppElement("#root");

const App = () => {
  const appConfig: AppConfig = useMemo(() => ({
    ...defaultAppConfig,
    pageTitlePrefix: (config.environment && config.environment !== "production") ? `[${config.environment.toUpperCase()}] ` : "",
  }), []);

  return (
    <Mimic2ContextProvider themeModeProviderProps={{ useAutoTheme: false }}>
      <ModalProvider>
        <PathPermissionsContextProvider>
          <AppConfigContext.Provider value={appConfig}>
            <MimicContextProvider>
              <ClientContextProvider>
                <AppVersionChecker />
                <ApiProvider>
                  <BreadcrumbsContextProvider>
                    <ClientContext.Consumer>
                      {client => {
                        return (
                          <Helmet>
                            <title>
                              {(() => {
                                switch (client) {
                                  case "mediDrive":
                                    return "MediDrive Affiliate Portal";
                                  default:
                                    return "CTG Affiliate portal";
                                }
                              })()}
                            </title>
                          </Helmet>
                        );
                      }}
                    </ClientContext.Consumer>
                    <Outlet />
                  </BreadcrumbsContextProvider>
                </ApiProvider>
              </ClientContextProvider>
            </MimicContextProvider>
          </AppConfigContext.Provider>
        </PathPermissionsContextProvider>
      </ModalProvider>
    </Mimic2ContextProvider>
  );
};

const Root = () => {
  const routes = useRoutesContext<RouteDescription>();

  const errorBoundaryRef = createRef<ErrorBoundary>();

  useEffect(() => {
    if (!!config.gtmId) {
      TagManager.initialize({ gtmId: config.gtmId });
    }
  }, []);

  useEffect(() => {
    window.addEventListener("error", e => {
      if (e.message === "ResizeObserver loop limit exceeded") {
        const resizeObserverErrDiv = document.getElementById(
          "webpack-dev-server-client-overlay-div",
        );
        const resizeObserverErr = document.getElementById(
          "webpack-dev-server-client-overlay",
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute("style", "display: none");
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute("style", "display: none");
        }
      }
    });
  }, []);

  return (
    <>
      <ErrorBoundary
        ref={errorBoundaryRef}
        fallbackRender={() => <GlobalErrorBanner supportLink={links.support} />}
      >
        <HistoryRouter history={createBrowserHistory({ window }) as any}>
          <Routes>
            {renderRoute([ {
              path: "/",
              element: <App />,
              children: routes,
            } ], RoutePermissionChecker)}
          </Routes>
        </HistoryRouter>
      </ErrorBoundary>
    </>
  );
};

createRoot(document.getElementById("root")!)
  .render(
    <RoutesContext.Provider value={routes}>
      <Root />
    </RoutesContext.Provider>,
  );
